import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Flex, Heading, Text, Button, Stack, HStack, Icon, Spinner, Badge } from "@chakra-ui/react";
import { ArrowLeft, MapPin, CheckCircle2, WifiOff, CloudUpload, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { FieldInput, isAnswered, displayAnswer } from "@/components/FieldInput";
import { loadDraft, saveDraft, clearDraft, queueSubmission, submitPayload, cachePut, cacheGet, findActionTemplate, safeQuery, safeAll } from "@/lib/offline";
import { QuickPill, InlineCommentBox, SavedNotes } from "@/components/QuickActions";
import { ContractorSignoffBox } from "@/components/ContractorSignoffBox";
import { showToast } from "@/components/AppToaster";
import { computeFlagReasons } from "@/lib/submissionMeta";
import { buildSubmissionPayload } from "@/lib/submissionPayload";
import { colors } from "@/theme/colors";

const OptionCard = ({ checked, onClick, title, sub, testId }) => (
  <Flex as="button" type="button" onClick={onClick} data-testid={testId} w="full" textAlign="left" align="center" gap="3"
    px="4" py="3.5" borderRadius="12px" border="1.5px solid"
    borderColor={checked ? colors.teal : colors.border} bg={checked ? colors.tealTint : colors.white}>
    <Icon as={MapPin} boxSize="5" color={checked ? colors.teal : colors.textSecondary} flexShrink={0} />
    <Box>
      <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{title}</Text>
      {sub && <Text fontSize="xs" color={colors.textSecondary}>{sub}</Text>}
    </Box>
  </Flex>
);

const LocationStep = ({ locations, locationId, onPick, errors }) => (
  <Stack gap="2.5" data-testid="location-step">
    <Text fontSize="sm" color={colors.textSecondary} mb="1">Where are you working right now?</Text>
    {locations.length === 0 ? (
      <HStack px="4" py="3" borderRadius="10px" bg="#FBEEDA" color="#8A5106">
        <Icon as={AlertTriangle} boxSize="4" />
        <Text fontSize="sm">No locations available. Ask your admin to assign you to a job site.</Text>
      </HStack>
    ) : (
      locations.map((l) => (
        <OptionCard key={l.id} checked={locationId === l.id} onClick={() => onPick(l.id)}
          title={l.name} sub={l.address} testId={`loc-option-${l.id}`} />
      ))
    )}
    {errors.includes("__location") && <Text fontSize="sm" color={colors.criticalText} data-testid="location-error">Please choose your location first.</Text>}
  </Stack>
);

const requiredMsg = (type) => {
  if (type === "signature") return "A signature is required.";
  if (type === "photo") return "At least one photo is required.";
  return "This answer is required.";
};

// Revise flow: prefilled photo answers only carry a storage path (dataUrl is
// stripped at upload). Attach fresh signed URLs so thumbnails render.
const signPathPhotos = async (answersObj, schema) => {
  const ids = [];
  (schema?.sections || []).forEach((s) => (s.fields || []).forEach((fl) => { if (fl.type === "photo") ids.push(fl.id); }));
  if (Array.isArray(answersObj._qa_photos)) ids.push("_qa_photos");
  const paths = [];
  ids.forEach((fid) => (Array.isArray(answersObj[fid]) ? answersObj[fid] : []).forEach((p) => {
    if (p?.path && !p.dataUrl) paths.push(p.path);
  }));
  if (!paths.length) return answersObj;
  const { data } = await supabase.storage.from("submission-photos").createSignedUrls(paths, 3600);
  const byPath = Object.fromEntries((data || []).filter((r) => r.signedUrl).map((r) => [r.path, r.signedUrl]));
  const next = { ...answersObj };
  ids.forEach((fid) => {
    if (!Array.isArray(next[fid])) return;
    next[fid] = next[fid].map((p) => (p?.path && byPath[p.path] ? { ...p, signedUrl: byPath[p.path] } : p));
  });
  return next;
};

const SectionStep = ({ sec, step, answers, onAnswer, errors, listsById, workers, qa }) => {
  const secTitle = sec.title || `Section ${step}`;
  const secScope = { sectionId: sec.id, sectionTitle: secTitle };
  return (
    <Stack gap="5" data-testid={`section-step-${step}`}>
      <Box>
        <Flex justify="space-between" align="center" gap="3">
          <Text fontWeight="700" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em" color={colors.tealHover}>
            {secTitle}
          </Text>
          {qa.pill(secScope)}
        </Flex>
        {qa.commentBoxIfOpen(secScope)}
        <SavedNotes notes={qa.notesFor(secScope)} />
      </Box>
      {(sec.fields || []).map((fl) => {
        const fScope = fl.type === "info" ? null
          : { sectionId: sec.id, sectionTitle: secTitle, fieldId: fl.id, fieldLabel: fl.label };
        return (
          <Box key={fl.id} data-testid={`fill-field-${fl.id}`}>
            {fl.type !== "info" && (
              <HStack gap="1" mb="2">
                <Text fontSize="sm" fontWeight="600" color={colors.textPrimary}>{fl.label}</Text>
                {fl.required && <Text color={colors.poppy}>*</Text>}
              </HStack>
            )}
            {/* Per-line-item quick-action pills removed (they sat too close to
                the N/A buttons and misfired) — the section-level pill above
                covers notes/photos for everything in this section. */}
            <FieldInput field={fl} value={answers[fl.id]} onChange={(v) => onAnswer(fl.id, v)}
              listsById={listsById} workers={workers} />
            {fScope && <SavedNotes notes={qa.notesFor(fScope)} />}
            {errors.includes(fl.id) && (
              <Text fontSize="sm" color={colors.criticalText} mt="1.5" data-testid={`field-error-${fl.id}`}>
                {requiredMsg(fl.type)}
              </Text>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default function FillForm() {
  // Key by template so navigating form -> linked action form fully remounts
  // (fresh state; the autosave/draft effects never cross templates).
  const { templateId } = useParams();
  return <FillFormInner key={templateId} templateId={templateId} />;
}

function FillFormInner({ templateId }) {
  const [searchParams] = useSearchParams();
  const reviseId = searchParams.get("revise");
  const draftId = reviseId ? `${templateId}_rev_${reviseId}` : templateId;
  const navigate = useNavigate();
  const { user, profile, activeCompanyId, hasFeature } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tpl, setTpl] = useState(null);
  const [revising, setRevising] = useState(null); // original submission being revised
  const [lists, setLists] = useState([]);
  const [locations, setLocations] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [myWorker, setMyWorker] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // 'sent' | 'queued'
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftWarn, setDraftWarn] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const restored = useRef(false);
  const [quick, setQuick] = useState({ comments: [], photos: [] });
  const [openComment, setOpenComment] = useState(null);
  const [signoffEnabled, setSignoffEnabled] = useState(false);
  const [contractorList, setContractorList] = useState([]);
  const [signoffs, setSignoffs] = useState([]);
  const clientIdRef = useRef(crypto.randomUUID());

  // Raised-from-another-form linkage (Quick Actions flag)
  const parentId = searchParams.get("parent");
  const parentTitle = searchParams.get("ptitle");
  const parentSection = searchParams.get("psec");
  const parentSectionId = searchParams.get("psid");
  const parentLoc = searchParams.get("ploc") || searchParams.get("loc");
  const assignmentId = searchParams.get("assignment");

  useEffect(() => {
    (async () => {
      const [tRes, slRes, locRes, wkRes] = await safeAll([
        supabase.from("templates").select("*").eq("id", templateId).maybeSingle(),
        supabase.from("select_lists").select("*").or(`company_id.is.null,company_id.eq.${activeCompanyId}`),
        supabase.from("locations").select("id,name,address,active").eq("active", true)
          .eq("company_id", activeCompanyId).order("name"),
        supabase.from("workers").select("id,full_name").eq("profile_id", user.id)
          .eq("company_id", activeCompanyId).limit(1),
      ]);
      let t = tRes.data, sl = slRes.data, locs = locRes.data, wk = wkRes.data;
      // Acting-company boundary: never open another company's template by direct link.
      if (t && t.company_id && t.company_id !== activeCompanyId) t = null;
      if (tRes.error || slRes.error || locRes.error || wkRes.error) {
        // Offline: open a NEW form from the cached template schemas + form context.
        const ctx = cacheGet(user.id, "fill_ctx") || {};
        const homeSnap = cacheGet(user.id, "home") || {};
        const formsSnap = cacheGet(user.id, "forms") || {};
        const cachedTpls = [...(homeSnap.templates || []), ...(formsSnap.templates || [])];
        const firstNonEmpty = (...xs) => xs.find((x) => Array.isArray(x) && x.length) || [];
        t = t || cachedTpls.find((x) => x.id === templateId) || null;
        sl = firstNonEmpty(sl, ctx.lists, homeSnap.lists, formsSnap.lists);
        locs = firstNonEmpty(locs, ctx.locations, homeSnap.locations, formsSnap.locations);
        wk = wk || (ctx.worker ? [ctx.worker] : []);
      } else {
        // Never poison the cache: keep the last known worker if this load came back empty.
        const prevWorker = (cacheGet(user.id, "fill_ctx") || {}).worker || null;
        cachePut(user.id, "fill_ctx", { lists: sl || [], locations: locs || [], worker: wk?.[0] || prevWorker });
      }
      let effectiveTpl = t || null;

      // Revise & resubmit: re-open the ORIGINAL submission's snapshot, pre-filled.
      let orig = null;
      if (reviseId) {
        const { data: sub } = await supabase.from("submissions").select("*").eq("id", reviseId).maybeSingle();
        if (sub && sub.submitted_by === user.id && sub.status === "pending") {
          orig = sub;
          effectiveTpl = {
            id: sub.template_id, title: sub.template_title, type: sub.template_type,
            version: sub.template_version, schema: sub.template_schema, status: "published",
          };
        }
      }
      setRevising(orig);
      setTpl(effectiveTpl);
      setLists(sl || []);
      setLocations(locs || []);
      setMyWorker(wk?.[0] || null);

      const needsWorkers = (effectiveTpl?.schema?.sections || []).some((s) =>
        (s.fields || []).some((fl) => fl.type === "select_worker" || fl.type === "select_workers"));
      if (needsWorkers) {
        // Offline-first (F1): in-form worker fields must fill in airplane mode.
        // Cache the roster on success; fall back to it (or the pickers snapshot)
        // when the live RPC can't reach the network.
        const { data: names, error: nmErr } = activeCompanyId
          ? await supabase.rpc("company_worker_names", { company: activeCompanyId })
          : { data: null, error: null };
        if (!nmErr && names) {
          setWorkers(names);
          const ctx = cacheGet(user.id, "fill_ctx") || {};
          cachePut(user.id, "fill_ctx", { ...ctx, workerNames: names });
        } else {
          const ctx = cacheGet(user.id, "fill_ctx") || {};
          const pk = cacheGet(user.id, "pickers") || {};
          setWorkers(ctx.workerNames || pk.workers || []);
        }
      }

      if (!restored.current) {
        restored.current = true;
        let initialAnswers = null;
        if (orig) {
          // Prefill from the original; then an in-progress revision draft (if any) wins.
          initialAnswers = orig.answers || {};
          setAnswers(initialAnswers);
          if ((locs || []).some((l) => l.id === orig.location_id)) setLocationId(orig.location_id);
        }
        if (parentLoc && !orig && (locs || []).some((l) => l.id === parentLoc)) setLocationId(parentLoc);
        const draft = await loadDraft(user.id, draftId);
        if (draft) {
          initialAnswers = draft.answers || {};
          setAnswers(initialAnswers);
          if (draft.locationId) setLocationId(draft.locationId);
          if (typeof draft.step === "number") setStep(draft.step);
          if (draft.quick) setQuick(draft.quick);
          if (draft.signoffs) setSignoffs(draft.signoffs);
          if (draft.clientId) clientIdRef.current = draft.clientId;
          setDraftRestored(true);
          setTimeout(() => setDraftRestored(false), 5000);
        }
        if (initialAnswers && effectiveTpl) {
          const base = initialAnswers;
          signPathPhotos(base, effectiveTpl.schema).then((signed) => {
            if (signed === base) return;
            // Only replace fields the user hasn't touched since prefill.
            setAnswers((cur) => {
              const next = { ...cur };
              let changed = false;
              Object.keys(signed).forEach((k) => {
                if (signed[k] !== base[k] && cur[k] === base[k]) { next[k] = signed[k]; changed = true; }
              });
              return changed ? next : cur;
            });
          });
        }
      }
      setLoading(false);
    })();
  }, [templateId, user.id, activeCompanyId, reviseId, draftId, parentLoc]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Contractor Sign-Off box: only for templates the Org Admin enabled it on.
  useEffect(() => {
    if (!activeCompanyId || !hasFeature("contractors")) return;
    (async () => {
      const { data: cfg } = await supabase.from("contractor_form_settings").select("signoff_enabled")
        .eq("company_id", activeCompanyId).eq("template_id", templateId).maybeSingle();
      if (!cfg?.signoff_enabled) return;
      const { data: cs } = await supabase.from("contractors").select("id,name,trade")
        .eq("company_id", activeCompanyId).eq("status", "active").order("name");
      setContractorList(cs || []);
      setSignoffEnabled(true);
    })();
  }, [activeCompanyId, templateId, hasFeature]);

  // Autosave draft so nothing is lost if the connection (or the app) dies.
  useEffect(() => {
    if (loading || done) return;
    saveDraft(user.id, draftId, { answers, locationId, step, quick, signoffs, clientId: clientIdRef.current }).then((ok) => setDraftWarn(!ok));
  }, [answers, locationId, step, quick, signoffs, loading, done, user.id, draftId]);

  const sections = useMemo(() => tpl?.schema?.sections || [], [tpl]);
  const totalSteps = sections.length + 2; // location + sections + review
  const listsById = useMemo(() => Object.fromEntries(lists.map((l) => [l.id, l])), [lists]);
  const reviewSections = useMemo(
    () => sections.map((sec) => ({ ...sec, reviewFields: (sec.fields || []).filter((fl) => fl.type !== "info") })),
    [sections]
  );

  const setAnswer = useCallback((fid, v) => setAnswers((a) => ({ ...a, [fid]: v })), []);

  // ---- Quick Actions (pill on report / section / field) ----
  const scopeId = (scope) => scope?.fieldId || scope?.sectionId || null;
  const scopeTitle = (scope) => scope?.fieldId
    ? `${scope.sectionTitle} — ${scope.fieldLabel}` : (scope?.sectionTitle || null);
  const scopeKey = (scope) => scopeId(scope) || "report";
  const toggleComment = (scope) => setOpenComment((k) => (k === scopeKey(scope) ? null : scopeKey(scope)));
  const addComment = (scope, body) => {
    setQuick((q) => ({
      ...q,
      comments: [...q.comments, { id: crypto.randomUUID(), section_id: scopeId(scope), section_title: scopeTitle(scope), body, created_at: new Date().toISOString() }],
    }));
    setOpenComment(null);
  };
  const addPhotos = (photos, scope) => {
    setQuick((q) => ({
      ...q,
      photos: [...q.photos, ...photos.map((p) => ({ ...p, id: crypto.randomUUID(), section_id: scopeId(scope), section_title: scopeTitle(scope) }))],
    }));
    showToast(photos.length > 1 ? `${photos.length} Photos Added Successfully` : "Photo Added Successfully");
  };
  const raiseAction = async (type, scope) => {
    await saveDraft(user.id, draftId, { answers, locationId, step, quick, clientId: clientIdRef.current });
    const t = await findActionTemplate(supabase, user.id, type, activeCompanyId);
    if (!t) { showToast(`The ${type} form isn't available on this device yet — open it once while online.`); return; }
    const params = new URLSearchParams({
      parent: clientIdRef.current, ptitle: tpl.title,
      psec: scopeTitle(scope) || "Whole report", psid: scopeId(scope) || "",
      ploc: locationId || "",
    });
    navigate(`/forms/fill/${t.id}?${params.toString()}`);
  };
  const countsFor = (scope) => {
    const sid = scopeId(scope);
    return {
      comments: quick.comments.filter((c) => (c.section_id || null) === sid).length,
      photos: quick.photos.filter((p) => (p.section_id || null) === sid).length,
    };
  };
  const notesFor = (scope) => quick.comments.filter((c) => (c.section_id || null) === scopeId(scope));
  const pill = (scope) => (
    <QuickPill scope={scope} counts={countsFor(scope)} commentOpen={openComment === scopeKey(scope)}
      onToggleComment={toggleComment} onAddPhotos={addPhotos} onRaise={raiseAction} />
  );
  const commentBoxIfOpen = (scope) => openComment === scopeKey(scope) && (
    <InlineCommentBox scopeKey={scopeKey(scope)} onSave={(body) => addComment(scope, body)} onClose={() => setOpenComment(null)} />
  );
  const qa = { pill, commentBoxIfOpen, notesFor, countsFor, isCommentOpen: (scope) => openComment === scopeKey(scope) };

  const validateSection = (sec) => {
    const missing = (sec.fields || []).filter((fl) => fl.required && !isAnswered(fl, answers[fl.id]));
    setErrors(missing.map((fl) => fl.id));
    return missing.length === 0;
  };

  const next = () => {
    if (step === 0) {
      if (!locationId) { setErrors(["__location"]); return; }
    } else if (step <= sections.length) {
      if (!validateSection(sections[step - 1])) return;
    }
    setErrors([]);
    setStep(step + 1);
    window.scrollTo({ top: 0 });
  };

  const back = () => { setErrors([]); setStep(Math.max(0, step - 1)); window.scrollTo({ top: 0 }); };

  const submit = async () => {
    // final guard: every required field across all sections
    for (const sec of sections) {
      const missing = (sec.fields || []).filter((fl) => fl.required && !isAnswered(fl, answers[fl.id]));
      if (missing.length) {
        setStep(sections.indexOf(sec) + 1);
        setErrors(missing.map((fl) => fl.id));
        return;
      }
    }
    const loc = locations.find((l) => l.id === locationId);
    const flagReasons = computeFlagReasons(tpl.schema, answers, listsById);
    const payload = buildSubmissionPayload({
      clientId: clientIdRef.current, activeCompanyId, tpl, locationId, loc, answers,
      quick, signoffs, user, profile, myWorker, revising,
      parentId, parentTitle, parentSectionId, parentSection, flagReasons,
    });
    setSubmitting(true);
    try {
      if (!navigator.onLine) throw new Error("offline");
      await submitPayload(supabase, payload);
      await clearDraft(user.id, draftId);
      if (assignmentId) {
        await supabase.from("form_assignments")
          .update({ status: "completed", submission_id: payload.id, completed_at: new Date().toISOString() })
          .eq("id", assignmentId);
      }
      setDone("sent");
    } catch (e) {
      if (!navigator.onLine || e.message === "offline" || e instanceof TypeError || /fetch|network/i.test(e.message || "")) {
        await queueSubmission(user.id, payload);
        await clearDraft(user.id, draftId);
        setDone("queued");
      } else {
        setErrors([]);
        setSubmitting(false);
        window.alert(`Could not submit: ${e.message}`);
        return;
      }
    }
    setSubmitting(false);
  };

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  if (!tpl || tpl.status !== "published") {
    return (
      <Box data-testid="fill-notfound">
        <Text color={colors.textSecondary}>This form isn't available.</Text>
        <Button mt="4" size="sm" variant="outline" onClick={() => navigate("/forms")}><ArrowLeft size={15} /> Back to forms</Button>
      </Box>
    );
  }

  if (done) {
    return <SuccessScreen done={done} revising={revising} tpl={tpl} parentId={parentId} navigate={navigate}
      onFillAnother={() => { setAnswers({}); setQuick({ comments: [], photos: [] }); setSignoffs([]); setStep(0); setDone(null); }} />;
  }

  const progress = Math.round((step / (totalSteps - 1)) * 100);
  const stepTitle = (() => {
    if (step === 0) return "Location";
    if (step <= sections.length) return sections[step - 1].title || `Section ${step}`;
    return "Review & submit";
  })();

  return (
    <Box data-testid="fill-page" maxW="560px" mx="auto" pb="24">
      <Flex align="center" gap="2" mb="3">
        <Button size="xs" variant="ghost" onClick={() => navigate("/forms")} data-testid="fill-back-to-forms">
          <ArrowLeft size={14} />
        </Button>
        <Box flex="1">
          <HStack gap="1.5">
            <Text fontSize="xs" color={colors.textSecondary}>{tpl.title}</Text>
            {revising && <Badge bg={colors.tealTint} color={colors.tealHover} borderRadius="full" px="2" fontSize="10px" data-testid="revising-badge">Revising — Rev {(revising.revision || 1) + 1}</Badge>}
          </HStack>
          <Text fontWeight="700" fontSize="md" color={colors.textPrimary}>{stepTitle}</Text>
        </Box>
        {!online && <Icon as={WifiOff} boxSize="4" color="#8A5106" data-testid="fill-offline-icon" />}
        {pill(null)}
        <Text fontSize="xs" color={colors.textSecondary}>{step + 1}/{totalSteps}</Text>
      </Flex>

      {commentBoxIfOpen(null) && <Box mb="4">{commentBoxIfOpen(null)}</Box>}

      {/* Progress bar */}
      <Box h="6px" borderRadius="full" bg={colors.cream} mb="5" data-testid="progress-bar">
        <Box h="full" borderRadius="full" bg={colors.teal} w={`${Math.max(progress, 4)}%`} transition="width 0.3s ease" />
      </Box>

      {parentId && (
        <HStack mb="4" px="4" py="2.5" borderRadius="10px" bg={colors.tealTint} color={colors.tealHover} data-testid="linked-parent-banner">
          <Icon as={AlertTriangle} boxSize="4" />
          <Text fontSize="sm">Linked to <b>{parentTitle}</b>{parentSection ? ` — ${parentSection}` : ""}. This action stays traceable to that report.</Text>
        </HStack>
      )}

      {draftRestored && (
        <HStack mb="4" px="4" py="2.5" borderRadius="10px" bg={colors.tealTint} color={colors.tealHover} data-testid="draft-restored-banner">
          <Icon as={CloudUpload} boxSize="4" />
          <Text fontSize="sm">Your earlier answers were restored.</Text>
        </HStack>
      )}

      {draftWarn && (
        <HStack mb="4" px="4" py="2.5" borderRadius="10px" bg="#FBEEDA" color="#8A5106" data-testid="draft-quota-banner">
          <Icon as={AlertTriangle} boxSize="4" />
          <Text fontSize="sm">Your photos are too large to keep in the offline draft — keep this page open until you submit.</Text>
        </HStack>
      )}

      {step === 0 ? (
        <LocationStep locations={locations} locationId={locationId}
          onPick={(lid) => { setLocationId(lid); setErrors([]); }} errors={errors} />
      ) : step <= sections.length ? (
        <SectionStep sec={sections[step - 1]} step={step} answers={answers}
          onAnswer={(fid, v) => { setAnswer(fid, v); setErrors((e) => e.filter((x) => x !== fid)); }}
          errors={errors} listsById={listsById} workers={workers} qa={qa} />
      ) : (
        <ReviewStep locations={locations} locationId={locationId} reviewSections={reviewSections}
          answers={answers} quick={quick} signoffEnabled={signoffEnabled} contractorList={contractorList}
          signoffs={signoffs} onSignoffsChange={setSignoffs}
          submitterName={myWorker?.full_name || profile?.full_name || user.email} />
      )}

      {/* Sticky bottom nav — z-index + bottom padding keep buttons clear of overlay widgets */}
      <Flex position="fixed" bottom="0" left="0" right="0" zIndex={2147483000} bg={colors.white}
        borderTop="1px solid" borderColor={colors.border} px="4" pt="3" pb={{ base: "6", md: "3" }} justify="center">
        <HStack gap="2" w="full" maxW="560px" pr={{ base: "2", md: "0" }}>
          {step > 0 && (
            <Button variant="outline" px="6" onClick={back} data-testid="wizard-back-btn">Back</Button>
          )}
          {step < totalSteps - 1 ? (
            <Button flex="1" onClick={next} data-testid="wizard-next-btn">
              {step === 0 ? "Start form" : "Next"}
            </Button>
          ) : (
            <Button flex="1" onClick={submit} loading={submitting} data-testid="wizard-submit-btn">
              {online ? "Submit" : "Save — send when online"}
            </Button>
          )}
        </HStack>
      </Flex>
    </Box>
  );
}

const successHeading = (done, revising) => {
  if (done !== "sent") return "Saved — will send when online";
  return revising ? "Revision submitted!" : "Submitted!";
};

const successMessage = (done, revising, tpl) => {
  if (done !== "sent") return `You're offline. ${tpl.title} is safely queued on this phone and will submit automatically once you reconnect.`;
  if (revising) return `Your revision (Rev ${(revising.revision || 1) + 1}) of ${tpl.title} was submitted. The original is kept and marked as superseded.`;
  return `${tpl.title} was submitted and is pending review.`;
};

const SuccessScreen = ({ done, revising, tpl, parentId, navigate, onFillAnother }) => {
  const heading = successHeading(done, revising);
  const message = successMessage(done, revising, tpl);
  return (
    <Flex minH="60vh" align="center" justify="center" data-testid="success-screen">
      <Stack align="center" gap="4" textAlign="center" maxW="360px">
        <Icon as={done === "sent" ? CheckCircle2 : CloudUpload} boxSize="14" color={done === "sent" ? colors.sageDeep : colors.teal} />
        <Heading fontFamily="heading" fontSize="xl" color={colors.textPrimary}>{heading}</Heading>
        <Text fontSize="sm" color={colors.textSecondary}>{message}</Text>
        <HStack gap="2" flexWrap="wrap" justify="center">
          {parentId && (
            <Button variant="outline" onClick={() => navigate(-1)} data-testid="back-to-parent-btn">Back to the form you came from</Button>
          )}
          <Button onClick={() => navigate("/forms")} data-testid="back-to-forms-btn">Back to forms</Button>
          <Button variant="outline" onClick={onFillAnother} data-testid="fill-another-btn">Fill another</Button>
        </HStack>
      </Stack>
    </Flex>
  );
};

const ReviewStep = ({ locations, locationId, reviewSections, answers, quick,
  signoffEnabled, contractorList, signoffs, onSignoffsChange, submitterName }) => (
  <Stack gap="4" data-testid="review-step">
    <HStack px="4" py="3" borderRadius="10px" bg={colors.white} border="1px solid" borderColor={colors.border}>
      <Icon as={MapPin} boxSize="4" color={colors.teal} />
      <Text fontSize="sm" color={colors.textPrimary} fontWeight="500">{locations.find((l) => l.id === locationId)?.name}</Text>
    </HStack>
    {reviewSections.map((sec) => (
      <Box key={sec.id} bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4">
        <Text fontWeight="700" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em" color={colors.tealHover} mb="2">{sec.title || "Section"}</Text>
        <Stack gap="2">
          {sec.reviewFields.map((fl) => (
            <Box key={fl.id}>
              <Flex justify="space-between" gap="3">
                <Text fontSize="sm" color={colors.textSecondary}>{fl.label}</Text>
                <Text fontSize="sm" color={colors.textPrimary} fontWeight="500" textAlign="right">{displayAnswer(fl, answers[fl.id])}</Text>
              </Flex>
              {fl.type === "photo" && Array.isArray(answers[fl.id]) && answers[fl.id].length > 0 && (
                <Flex gap="2" flexWrap="wrap" mt="1.5" data-testid={`review-photos-${fl.id}`}>
                  {answers[fl.id].map((p, i) => (p.dataUrl || p.signedUrl) && (
                    <img key={p.path || p.id || i} src={p.dataUrl || p.signedUrl} alt={p.name || "photo"}
                      style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: `1px solid ${colors.border}` }} />
                  ))}
                </Flex>
              )}
            </Box>
          ))}
        </Stack>
      </Box>
    ))}
    {(quick.comments.length > 0 || quick.photos.length > 0) && (
      <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4" data-testid="review-quick-actions">
        <Text fontWeight="700" fontSize="xs" textTransform="uppercase" letterSpacing="0.05em" color={colors.tealHover} mb="2">Quick notes & photos</Text>
        <Stack gap="2">
          {quick.comments.map((c) => (
            <Box key={c.id}>
              <Text fontSize="xs" color={colors.textSecondary}>{c.section_title || "Whole report"}</Text>
              <Text fontSize="sm" color={colors.textPrimary}>{c.body}</Text>
            </Box>
          ))}
          {quick.photos.length > 0 && (
            <Flex gap="2" flexWrap="wrap">
              {quick.photos.map((p) => (
                <img key={p.id} src={p.dataUrl} alt={p.name} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8 }} />
              ))}
            </Flex>
          )}
        </Stack>
      </Box>
    )}
    {signoffEnabled && (
      <ContractorSignoffBox contractors={contractorList} signoffs={signoffs} onChange={onSignoffsChange} />
    )}
    <Text fontSize="xs" color={colors.textSecondary}>
      Submitting as <b>{submitterName}</b>. Submissions are permanent and can't be edited afterwards.
    </Text>
  </Stack>
);
